import { supabase } from '@/lib/supabase';
import type {
  Customer,
  CustomerListItem,
  CreateCustomerInput,
  UpdateCustomerInput,
} from '../types';

/**
 * Fetch all customers for a business using the v_customers_list view
 */
export async function fetchCustomers(
  businessId: string
): Promise<CustomerListItem[]> {
  const { data, error } = await supabase
    .from('v_customers_list')
    .select('*')
    .eq('business_id', businessId)
    .order('full_name', { ascending: true });

  if (error) {
    console.error('[customersApi] Error fetching customers:', error);
    throw new Error(error.message);
  }

  return data || [];
}

/**
 * Search customers by name or email
 */
export async function searchCustomers(
  businessId: string,
  searchTerm: string
): Promise<CustomerListItem[]> {
  const { data, error } = await supabase
    .from('v_customers_list')
    .select('*')
    .eq('business_id', businessId)
    .or(`full_name.ilike.%${searchTerm}%,email.ilike.%${searchTerm}%`)
    .order('full_name', { ascending: true })
    .limit(20);

  if (error) {
    console.error('[customersApi] Error searching customers:', error);
    throw new Error(error.message);
  }

  return data || [];
}

/**
 * Check if a customer with the given email already exists for a business
 * Returns the customer if found, null otherwise
 */
export async function checkCustomerByEmail(
  businessId: string,
  email: string
): Promise<CustomerListItem | null> {
  const { data, error } = await supabase
    .from('v_customers_list')
    .select('*')
    .eq('business_id', businessId)
    .ilike('email', email.trim())
    .limit(1)
    .maybeSingle();

  if (error) {
    console.error('[customersApi] Error checking customer email:', error);
    throw new Error(error.message);
  }

  return data;
}

/**
 * Fetch a single customer by ID
 */
export async function fetchCustomerById(
  customerId: string
): Promise<Customer | null> {
  const { data, error } = await supabase
    .from('customers')
    .select('*')
    .eq('id', customerId)
    .single();

  if (error) {
    if (error.code === 'PGRST116') {
      // No rows returned
      return null;
    }
    console.error('[customersApi] Error fetching customer:', error);
    throw new Error(error.message);
  }

  return data;
}

/**
 * Create a new customer
 */
export async function createCustomer(
  input: CreateCustomerInput
): Promise<Customer> {
  const { data, error } = await supabase
    .from('customers')
    .insert({
      business_id: input.business_id,
      first_name: input.first_name,
      last_name: input.last_name,
      email: input.email,
      phone: input.phone || null,
      billing_street_address: input.billing_street_address,
      billing_unit: input.billing_unit || null,
      billing_city: input.billing_city,
      billing_province: input.billing_province,
      billing_postal_code: input.billing_postal_code,
      billing_country: input.billing_country,
    })
    .select()
    .single();

  if (error) {
    console.error('[customersApi] Error creating customer:', error);
    throw new Error(error.message);
  }

  return data;
}

/**
 * Update an existing customer
 */
export async function updateCustomer(
  customerId: string,
  input: UpdateCustomerInput
): Promise<Customer> {
  const { data, error } = await supabase
    .from('customers')
    .update(input)
    .eq('id', customerId)
    .select()
    .single();

  if (error) {
    console.error('[customersApi] Error updating customer:', error);
    throw new Error(error.message);
  }

  return data;
}

/**
 * Delete a customer (only if no projects reference it)
 */
export async function deleteCustomer(customerId: string): Promise<void> {
  const { error } = await supabase
    .from('customers')
    .delete()
    .eq('id', customerId);

  if (error) {
    console.error('[customersApi] Error deleting customer:', error);
    throw new Error(error.message);
  }
}